import { environment } from '../../../environments/environment';

export const getSubdomain = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = window.location.hostname;
  return extractSubdomain(hostname);
};

export const extractSubdomain = (hostname: string): string => {
  // If it's an IP address, we don't have a subdomain
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return '';
  }

  const parts = hostname.split('.');

  // If localhost, it's either localhost or subdomain.localhost
  if (hostname.includes('localhost')) {
    if (parts.length > 1) {
      return parts[0];
    }
    return '';
  }

  // Handle typical domains (e.g. kimbocare.com => 2 parts, kimbocare.dev => 2 parts)
  // If we have something like tenant.kimbocare.com => 3 parts
  if (parts.length > 2) {
    // If we have a www, we could consider it as no tenant subdomain or treat 'www' as special
    const subdomain = parts[0];
    if (subdomain === 'www') {
      return '';
    }
    return subdomain;
  }

  return '';
};

export const getCurrentBaseDomain = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  const hostname = window.location.hostname;

  // If IP address
  if (/^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
    return hostname;
  }

  const parts = hostname.split('.');

  // If localhost
  if (hostname.includes('localhost')) {
    return 'localhost';
  }

  // For real domains, we typically want the last two parts (e.g. kimbocare.com)
  if (parts.length > 2) {
    // This is a naive approach, might need refinement if using .co.uk etc.
    return parts.slice(-2).join('.');
  }

  return hostname;
};

export const redirectToTenant = (
  slug: string,
  authStatePayload?: string,
): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentSubdomain = getSubdomain();

  // If we are already on the right subdomain, do nothing
  if (currentSubdomain === slug) {
    return;
  }

  // Production hosts the tenants as sub-subdomains of the **root domain** —
  // e.g. `freedom.kimbocare.app`. Non-production environments keep the FULL
  // current host as the base so platform sub-subdomains survive — e.g. from
  // `hm.kimbocare.com` with slug `freedom` we get `freedom.hm.kimbocare.com`.
  // The discriminator is `environment.name` (the `production` flag is set
  // to `false` in environment.prod.ts and can't be trusted).
  const isProduction = environment.name === 'production';
  const baseHost = isProduction
    ? getCurrentBaseDomain()
    : window.location.hostname;
  const port = window.location.port ? `:${window.location.port}` : '';
  const protocol = window.location.protocol;
  let path = window.location.pathname;
  let search = window.location.search;

  if (authStatePayload) {
    path = '/loginsuccess';
    search = `?state=${authStatePayload}`;
  }

  // Construct the new URL
  const newUrl = `${protocol}//${slug}.${baseHost}${port}/#${path}${search}`;
  window.location.href = newUrl;
};
